import {
  Button,
  Center,
  ConditionalRendering,
  Loader,
  Text
} from "@marketplace/ui";
import { getRouteApi } from "@tanstack/react-router";
import { useAtomValue, useSetAtom } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdCheckCircle } from "react-icons/md";

import { Pagination } from "@/components";
import { usePermissions } from "@/hooks";

import { claimOrderSubmitModalAtom } from "../../atoms/claim-order-submit-modal-atom";
import { orderDetailModalAtom } from "../../atoms/order-detail-atom";
import { ClaimOrderFilter } from "../../components/claim-order-filter";
import { ClaimOrderSubmitModal } from "../../components/claim-order-submit-modal";
import { ClaimOrderTable } from "../../components/claim-order-table";
import { OrderDetailModal } from "../../components/order-detail-modal";
import type { ClaimOrderListItem } from "../../dtos/get-claim-order-list-response-dto";
import { useGetClaimOrderListQuery } from "../../hooks/queries/use-get-claim-order-list-query";

const route = getRouteApi("/disbursement/claim-orders/");

const NoClaimOrdersIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="120"
    height="120"
    viewBox="0 0 24 24"
    fill="none"
  >
    <circle
      cx="12"
      cy="12"
      r="10"
      stroke="#2F7DC8"
      strokeWidth="2"
      fill="none"
    />
    <line
      x1="4.93"
      y1="4.93"
      x2="19.07"
      y2="19.07"
      stroke="#2F7DC8"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const NoClaimOrders = () => {
  const { t } = useTranslation("order", { keyPrefix: "claimOrderPage" });

  return (
    <div className="flex flex-col justify-center items-center gap-y-4 mt-[56px]">
      <NoClaimOrdersIcon />
      <div className="text-center max-w-sm">
        <Text
          type="heading"
          size="xl"
          className="mb-2 text-fjord-grey-500"
          weight="semibold"
        >
          {t("emptyState.title")}
        </Text>
        <Text type="paragraph" className="text-fjord-grey-300">
          {t("emptyState.description")}
        </Text>
      </div>
    </div>
  );
};

export const ClaimOrderListPage = () => {
  const { page, q } = route.useSearch();
  const navigate = route.useNavigate();
  const { t } = useTranslation("order", { keyPrefix: "claimOrderPage" });
  const [selectedRecords, setSelectedRecords] = useState<ClaimOrderListItem[]>(
    []
  );

  const orderDetailModal = useAtomValue(orderDetailModalAtom);
  const resetOrderDetailModal = useResetAtom(orderDetailModalAtom);
  const setClaimOrderSubmitModal = useSetAtom(claimOrderSubmitModalAtom);

  const handleReadyToClaim = () => {
    setClaimOrderSubmitModal({
      visible: true,
      data: { orders: selectedRecords }
    });
  };

  const { data, isLoading } = useGetClaimOrderListQuery({
    queryParams: {
      page,
      limit: 10,
      q
    }
  });

  const records = data?.data ?? [];
  const metadata = data?.metadata;

  const { isSeller } = usePermissions();

  const selectedCount = selectedRecords.length;
  const hasSelection = selectedCount > 0;

  return (
    <>
      <OrderDetailModal
        opened={orderDetailModal.visible}
        onClose={resetOrderDetailModal}
      />
      <ClaimOrderSubmitModal onSuccess={() => setSelectedRecords([])} />

      <div className="flex justify-between items-center mb-6">
        <div>
          <Text type="heading" size="l" className="text-humble-blue-500">
            {t("title")}
          </Text>
          <Text className="text-fjord-grey-400">{t("description")}</Text>
        </div>
        <ConditionalRendering condition={isSeller}>
          <div className="flex items-center gap-x-4">
            <ConditionalRendering condition={hasSelection}>
              <Text
                type="heading"
                size="xs"
                className="text-fjord-grey-300"
                weight="semibold"
              >
                {t("selectedCount", { count: selectedCount })}
              </Text>
            </ConditionalRendering>
            <Button
              variant="primary"
              rightSection={<MdCheckCircle size={16} />}
              disabled={!hasSelection}
              onClick={handleReadyToClaim}
            >
              {t("readyToClaim")}
            </Button>
          </div>
        </ConditionalRendering>
      </div>

      <ClaimOrderFilter />

      <ConditionalRendering condition={isLoading}>
        <Center className="my-20">
          <Loader />
        </Center>
      </ConditionalRendering>

      <ConditionalRendering condition={!isLoading && records.length === 0}>
        <NoClaimOrders />
      </ConditionalRendering>

      <ConditionalRendering condition={!isLoading && records.length > 0}>
        <div className="space-y-6">
          <ClaimOrderTable
            records={records}
            page={page}
            {...(isSeller && {
              selectedRecords,
              onSelectedRecordsChange: setSelectedRecords
            })}
          />
          <Pagination
            currentPageTotal={records.length}
            totalData={metadata?.total}
            total={metadata?.totalPage ?? 0}
            value={metadata?.page}
            onChange={(value) =>
              navigate({
                search: (prev) => ({
                  ...prev,
                  page: value
                })
              })
            }
          />
        </div>
      </ConditionalRendering>
    </>
  );
};
